import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { ProfileSettingVisibility, ProfileSettings } from '@flipster/apis/v1/user/types';
import { useOverlayItem } from '@flipster/overlay';
import { Button, Toggle } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { alert } from '@flipster/utils/alert';
import { map } from '@flipster/utils/map';
import segment from '@flipster/utils/segment';
import { ProfileEvent } from '@flipster/utils/segment/events/profile';

import { profileSettingBunja } from '../../shared/community/profile';
import { profileBunja } from '../bunja';
import { booleanToVisibility, visibilityToBoolean } from '../misc';

interface PrivacySetting {
  title: string;
  description: string;
  type: keyof ProfileSettings;
  action: Record<ProfileSettingVisibility, { title: string; message: string }>;
}
const PrivacySettings: PrivacySetting[] = [
  {
    title: 'Performance',
    description: 'Let others view your top performing positions, including position size and P&L.',
    type: 'performanceVisibility',
    action: {
      PUBLIC: {
        title: 'Your performance is now public',
        message: 'Your performance including position size, and P&L are now visible to other users.',
      },
      PRIVATE: {
        title: 'Your performance will be set to private',
        message:
          'You won’t be able to view others’ performance, position size, and P&L. Your social links will also be hidden.',
      },
    },
  },
  {
    title: 'Trading activity',
    description:
      'Let others view your detailed trading activity, including position activities. In public mode, some activities may be shown in notifications for other users.',
    type: 'tradeHistoryVisibility',
    action: {
      PUBLIC: {
        title: 'Your trade activity is now public',
        message: 'Your detailed trading activity, including position activities, is now visible to other users.',
      },
      PRIVATE: {
        title: 'Your trading activity will be set to private',
        message:
          'You won’t be able to view others’ detailed trading activity, and your social links will also be hidden.',
      },
    },
  },
];

const ProfileSettingsModal = () => {
  const { onClose } = useOverlayItem();
  const { profileAtom } = useBunja(profileBunja);
  const profile = useAtomValue(profileAtom);
  const { putUserProfileMutationAtom } = useBunja(profileSettingBunja);
  const { mutateAsync, isPending, variables } = useAtomValue(putUserProfileMutationAtom);

  return (
    <Modal>
      <Modal.Close onClose={onClose} />
      <Modal.Header>
        <Modal.Title label='Profile settings' />
      </Modal.Header>
      <Modal.Content>
        <section className={stack({ gap: 16 })}>
          <div className={stack({ gap: 4 })}>
            <h3 className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-4' })}>Privacy Settings</h3>
            <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
              To add social links to your profile, both performance and trading activity must be public.
            </p>
          </div>
          <div className={stack({ borderRadius: 8, backgroundColor: 'background.default.level-2' })}>
            {PrivacySettings.map(({ title, description, type, action }) => {
              const pendingChecked = isPending && map(variables?.settings?.[type], visibilityToBoolean);
              const checked = (pendingChecked || map(profile?.mySettings[type], visibilityToBoolean)) ?? false;

              return (
                <div key={title} className={stack({ p: 16, gap: 8 })}>
                  <div className={hstack({ justify: 'space-between' })}>
                    <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-4' })}>{title}</p>
                    <Toggle
                      css={{ flexShrink: 0 }}
                      selected={checked}
                      onChange={async () => {
                        const next = !checked;
                        const nextVisibility = booleanToVisibility(next);
                        if (nextVisibility === 'PUBLIC') {
                          const { title, message } = action[nextVisibility];
                          await mutateAsync({
                            settings: { [type]: nextVisibility },
                          });
                          return alert.show({
                            title: (
                              <p className={css({ textStyle: 'Title/S', color: 'content.default.level-4' })}>{title}</p>
                            ),
                            message,
                            confirmText: 'Done',
                          });
                        }
                        if (nextVisibility === 'PRIVATE') {
                          const { title, message } = action[nextVisibility];
                          return alert.show({
                            title: (
                              <p
                                className={hstack({
                                  textStyle: 'Title/S',
                                  color: 'content.default.level-4',
                                  gap: 6,
                                  alignItems: 'self-start',
                                  tabletDown: {
                                    '& > svg': {
                                      width: 20,
                                      height: 20,
                                      flexShrink: 0,
                                    },
                                  },
                                })}
                              >
                                <CautionCircleIcon color={token('colors.content.default.level-3')} />
                                <span>{title}</span>
                              </p>
                            ),
                            message,
                            confirmText: 'Set to private',
                            onConfirm: () => mutateAsync({ settings: { [type]: nextVisibility } }),
                            closeText: 'Close',
                            withClose: true,
                          });
                        }
                      }}
                    />
                  </div>
                  <p
                    className={css({
                      w: { base: '100%', tablet: 306 },
                      textStyle: 'Text/M/Regular',
                      color: 'content.default.level-2',
                    })}
                  >
                    {description}
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      </Modal.Content>
      <Modal.Footer>
        <Button
          size='large'
          label='Done'
          onClick={() => {
            segment.track(ProfileEvent.ProfileSettingsDoneClick);
            onClose();
          }}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default ProfileSettingsModal;
